import { MouseEvent, useContext, useState } from "react";
import styled, { ThemeContext } from "styled-components";
import css from "@styled-system/css";
import {
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  space,
  SpaceProps,
} from "styled-system";

import { useIsMobileDevice } from "../useIsMobileDevice.hook";

import Box from "../Box/Box";
import LinkButton from "../Button/LinkButton";
import { LinkButtonProps } from "../Button/button.type";
import Icon from "../Icon/Icon";
import SearchBar from "../SearchForVehicles/SearchBar";
import Logo from "../Logo/Logo";

import NavItem from "./NavItem";
import MyBCA from "./MyBCA";
import MobileMenu from "./MobileMenu";
import { MenuLink } from "../Navigation/MenuLink.type";

import { HeaderProps } from "./bcaHeader.type";

const Row = styled.div<FlexboxProps & LayoutProps & SpaceProps>`
  display: flex;
  width: 100%;
  align-items: center;
  box-sizing: border-box;
  flex-wrap: wrap;
  ${flexbox}
  ${layout}
  ${space}
`;

const Ul = styled.ul<LayoutProps & FlexboxProps>`
  display: flex;
  align-items: center;
  padding: 0;
  margin: 6px 0 0 0;
  list-style: none;
  li:first-of-type > ul {
    @media only screen and (max-width: 1500px) {
      transform: translateX(-20%);
    }
  }
  li:first-of-type > ul::before {
    @media only screen and (max-width: 1500px) {
      left: calc(20% - 6px);
    }
  }

  & > li {
    margin-right: 20px;
  }

  @media only screen and (min-width: 900px) {
    & > li {
      margin-right: 30px;
    }
  }

  @media only screen and (min-width: 970px) {
    & > li {
      margin-right: 40px;
    }
  }

  ${flexbox}
  ${layout}
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 120px;
  padding: 16px 0 0 0;
  box-sizing: border-box;
  position: relative;
  ${css({
    backgroundColor: "secondary-nav-blue",
  })};
`;

const RegisterButton = ({ link, ...props }: LinkButtonProps) => {
  const themeContext = useContext(ThemeContext);

  const css = `
  color: ${themeContext.colors["primary-white"]};
  &:not([disabled]):hover {
      color: ${themeContext.colors["primary-light-blue"]};
  }`;

  return (
    <LinkButton
      variant="tertiary"
      text="Register"
      css={css}
      ml={24}
      paddingX={0}
      link={link}
      shouldOpenInNewWindow={false}
      {...props}
    ></LinkButton>
  );
};

const LoginButton = ({ link, ...props }: LinkButtonProps) => {
  const themeContext = useContext(ThemeContext);

  const css = `
    color: ${themeContext.colors["primary-white"]};
    border-color: ${themeContext.colors["primary-white"]};
    &:not([disabled]):hover {
        color: ${themeContext.colors["primary-navy-blue"]};
        border-color: ${themeContext.colors["primary-white"]};
        background: ${themeContext.colors["primary-white"]}; 
    }`;

  return (
    <LinkButton
      variant="secondary"
      text="Login"
      css={css}
      width={128}
      ml={24}
      link={link}
      shouldOpenInNewWindow={false}
      {...props}
    ></LinkButton>
  );
};

const TrackedButton = styled.a<LayoutProps>`
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2px;
  margin-right: 30px;
  margin-left: 30px;
  line-height: 24px;
  ${css({
    color: "primary-white",
    fontSize: "body",
    fontFamily: "normal",
    fontWeight: "semiBold",
  })}
  ${layout}
`;

const HamburgerButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  height: 21px;
  margin-right: 5px;
  cursor: pointer;
`;

const Anchor = styled.a`
  display: inline-block;
`;

const DEFAULT_REGISTER_URL = "https://www.bca.co.uk/buy/Join-BCA/Register/";

export default function BCAHeader({
  urls,
  user,
  navItems = [],
  myBCALinks = {},
  vehicleOptions = [],
  savedSearches = [],
  recentSearches = [],
  defaultSearchTerm = "",
}: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isMyBCAMenu, setIsMyBCAMenu] = useState<boolean>(false);
  const [isAuthenticated] = useState<boolean>(!!user);
  const [isTrackedSelected, setIsTrackedSelected] = useState(false);
  const [isMyBCASelected, setIsMyBCASelected] = useState(false);
  const registerButtonURL = urls.register || DEFAULT_REGISTER_URL;
  const isMobileDevice = useIsMobileDevice();

  const menuButtons = navItems
    .filter((item) => item.isVisible)
    .map((item: MenuLink, index: number) => {
      return <NavItem key={item.id} item={item} index={index}></NavItem>;
    });

  const maxHeaderWidth = {
    desktop: "maxGridWidth",
  };

  const showMyBCAButton =
    isMobileDevice || (!isMobileDevice && isAuthenticated);

  const handleMyBCAClick = (event: MouseEvent<HTMLElement>) => {
    if (!isMobileDevice) {
      return null;
    }

    event.preventDefault();
    setIsMyBCAMenu(true);
    setIsMenuOpen(true);
  };

  return (
    <Header>
      {isMenuOpen && (
        <MobileMenu
          isAuthenticated={isAuthenticated}
          isMyBCAMenu={isMyBCAMenu}
          navItems={navItems}
          myBCALinks={myBCALinks}
          user={user}
          urls={urls}
          onClose={() => {
            setIsMenuOpen(false);
          }}
        ></MobileMenu>
      )}
      <Row
        maxWidth={maxHeaderWidth}
        px={{ _: "mobileGridMargin", desktop: "desktopGridMargin" }}
      >
        <Box
          width={{
            _: 1 / 2,
            desktop: "auto",
          }}
          mr={{
            _: 0,
            desktop: 28,
          }}
          pl={{
            _: 3,
            desktop: 0,
          }}
          pb={{
            _: 2,
            desktop: 0,
          }}
        >
          <Anchor
            href="/"
            target="_top"
            rel="noopener noreferrer"
            aria-label="BCA Logo"
          >
            <Logo />
          </Anchor>
        </Box>
        <Box
          height="37px"
          width={{ _: 1 / 2, desktop: 0 }}
          display={{ _: "flex", desktop: "none" }}
          justifyContent="flex-end"
        >
          {isAuthenticated && (
            <a
              href="https://auctionview.bca.co.uk/Buyer/My/TrackedVehicles"
              onMouseEnter={() => setIsTrackedSelected(true)}
              onMouseLeave={() => setIsTrackedSelected(false)}
            >
              <Icon
                name="star"
                weight={isTrackedSelected ? "solid" : "light"}
                height={21}
                width={21}
                mr="20px"
                color="primary-white"
              />
            </a>
          )}

          {showMyBCAButton && (
            <a
              href="https://www.bca.co.uk/MyBCA/"
              onMouseEnter={() => setIsMyBCASelected(true)}
              onMouseLeave={() => setIsMyBCASelected(false)}
              onClick={handleMyBCAClick}
            >
              <Icon
                name="user-alt"
                weight={isMyBCASelected ? "solid" : "light"}
                color="primary-white"
                height={21}
                width={21}
                mr="20px"
              />
            </a>
          )}
          <HamburgerButton
            onClick={() => {
              setIsMyBCAMenu(false);
              setIsMenuOpen(true);
            }}
          >
            <Icon
              name="bars"
              weight="light"
              color="white"
              height={24}
              width={24}
            />
          </HamburgerButton>
        </Box>
        <SearchBar
          options={vehicleOptions}
          savedSearches={savedSearches}
          recentSearches={recentSearches}
          isAuthenticated={isAuthenticated}
          auctionViewRoot={urls.auctionViewRoot || ""}
          searchRootURL={urls.searchRootURL}
          defaultSearchTerm={defaultSearchTerm}
        />

        {isAuthenticated ? (
          <>
            <TrackedButton
              display={{ _: "none", desktop: "flex" }}
              href="https://auctionview.bca.co.uk/Buyer/My/TrackedVehicles"
              onMouseEnter={() => setIsTrackedSelected(true)}
              onMouseLeave={() => setIsTrackedSelected(false)}
              onFocus={() => setIsTrackedSelected(true)}
              onBlur={() => setIsTrackedSelected(false)}
            >
              <Icon
                name="star"
                weight={isTrackedSelected ? "solid" : "light"}
                color="primary-white"
                height={26}
                width={26}
              />
              Tracked
            </TrackedButton>
            <MyBCA
              links={myBCALinks}
              logOutUrl={urls.logout}
              user={user}
              display={{ _: "none", desktop: "flex" }}
              myBCALink={urls.myBCALink}
            ></MyBCA>
          </>
        ) : (
          <>
            <RegisterButton
              display={{ _: "none", desktop: "flex" }}
              link={registerButtonURL}
            ></RegisterButton>
            <LoginButton
              display={{ _: "none", desktop: "flex" }}
              link={urls.login}
              shouldOpenInNewWindow={false}
            ></LoginButton>
          </>
        )}
      </Row>
      <Row
        as="nav"
        role="navigation"
        maxWidth={maxHeaderWidth}
        display={{ _: "none", desktop: "flex" }}
        px={{ _: "mobileGridMargin", desktop: "desktopGridMargin" }}
      >
        <Ul flex={1} role="menubar" aria-label="BCA main menu">
          {menuButtons}
        </Ul>
      </Row>
    </Header>
  );
}
